import CounterBar from "@Components/game/counter-bar";
import GameDialog from "@Components/game/game-dialog";
import { AppContext, Events } from "@Context/common/general/context";
import Event from "@Helpers/event";
import { faChevronLeft, faChevronRight, faCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import useI18n from "@i18n";
import { Box, Button, CardMedia, Typography, useMediaQuery, useTheme } from "@mui/material";
import { clone, includes, isEmpty } from "lodash-es";
import { Fragment, useContext, useEffect, useState } from "react";

// Remove slides that either dont specify contexts or dont match the passed contexts
const filterSlidesConfig = (slidesConfig, contexts = []) => {
  let filteredSlides = [];
  slidesConfig.slides.forEach((slide) => {
    if (slide.visableContexts && !isEmpty(slide.visableContexts)) {
      let matched = false;
      slide.visableContexts.forEach((context) => {
        if (includes(contexts, context)) matched = true;
      });
      if (matched) filteredSlides.push(slide);
    } else {
      filteredSlides.push(slide);
    }
  });
  slidesConfig.slides = filteredSlides;
  return slidesConfig;
};

let AnimationTime = 0.5;

export default ({ open = false, slidesConfig = [], contexts, exitEvent = new Event() }) => {
  const { i18n } = useI18n();
  const { state } = useContext(AppContext);
  const [slideIndex, setSlideIndex] = useState(0);
  const [filteredSlidesConfig, setFilteredSlidesConfig] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [nextEnabled, setNextEnabled] = useState(true);
  const [previousEnabled, setPreviousEnabled] = useState(false);
  const [exitButtonVisible, setExitButtonVisible] = useState(false);
  const [backgroundOverride, setBackgroundOverride] = useState(null);
  const [animateOut, setAnimateOut] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [direction, setDirection] = useState("left");
  const [paperProps, setPaperProps] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isFullscreen = slidesConfig ? slidesConfig.fullscreen : false;
  const isScrollView = slidesConfig ? slidesConfig.scroll : false;
  const isArrowsEnabled = slidesConfig && slidesConfig.navArrows === false ? false : true;

  AnimationTime = isMobile ? 0.385 : 0.5;

  const scoreBarOverrides = {
    container: {
      backgroundColor: "transparent",
      justifyContent: "center",
      marginBottom: theme.spacing(5),
    },
    emptyBlock: {
      width: "30px",
      background: theme.palette.dsSurface.v300,
      borderLeft: "4px solid transparent",
    },
    blockFill: {
      width: "30px",
      background: theme.palette.dsSurface.v300,
      borderLeft: "4px solid transparent",
    },
    currentBlock: {
      width: "30px",
      background: theme.palette.dsSurface.v200,
      borderLeft: `1px solid ${theme.palette.dsSurface.v300}`,
      borderTop: `1px solid ${theme.palette.dsSurface.v300}`,
      borderBottom: `1px solid ${theme.palette.dsSurface.v300}`,
      borderRight: `1px solid ${theme.palette.dsSurface.v300}`,
    },
  };

  const textStyles = {
    text: {
      fontWeight: "600",
      textAlign: "center",
      color: "#4A6680",
      display: "block",
      paddingLeft: "15%",
      paddingRight: "15%",
      marginBottom: theme.spacing(3),
    },
    normal: {
      fontSize: theme.spacing(3.5),
      marginBottom: theme.spacing(0),
      ...(isMobile && {
        "@media (orientation: portrait)": {
          fontSize: theme.spacing(3),
        },
        "@media (orientation: landscape)": {
          fontSize: theme.spacing(2.5),
        },
      }),
    },
    large: {
      fontSize: theme.spacing(4),
      ...(isMobile && {
        "@media (orientation: portrait)": {
          fontSize: theme.spacing(3.5),
        },
        "@media (orientation: landscape)": {
          fontSize: theme.spacing(3),
        },
      }),
    },
    inline: {
      display: "inline-flex",
      marginLeft: theme.spacing(0.75),
      paddingLeft: 0,
      paddingRight: 0,
    },
    default: {
      color: "#4A6680",
    },
    primary: {
      color: "#0089B5",
    },
    secondary: {
      color: "#DE6C65",
    },
  };

  const imageStyles = {
    image: {
      fontSize: "28px",
      height: isFullscreen ? "50vh" : theme.spacing(37),
      width: "100%",
      maxWidth: "80vw",
      backgroundSize: "contain",
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(5),
      ...(isMobile && {
        marginTop: theme.spacing(0),
        marginBottom: theme.spacing(0),
        "@media (orientation: portrait)": {
          height: theme.spacing(37),
        },
        "@media (orientation: landscape)": {
          height: "40vh",
        },
      }),
    },
    marginTop: {
      marginTop: `${theme.spacing(2)} !important`,
    },
    marginBottom: {
      marginBottom: `${theme.spacing(2)} !important`,
    },
  };

  const fullPageStyles = {
    image: {
      width: state.device.isVR ? "56vw" : "100%",
      height: state.device.isVR ? "56vh" : "100%",
    },
  };

  useEffect(() => {
    const filteredSlides = filterSlidesConfig(clone(slidesConfig), contexts);
    setBackgroundOverride(filteredSlides.backgroundOverride || null);
    setCurrentSlide(filteredSlides.slides[0]);
    setFilteredSlidesConfig(filteredSlides);
    setExitButtonVisible(filteredSlides.slides.length === 1);
    setPaperProps(
      filteredSlides.slides[0].paper || {
        background:
          filteredSlides.backgroundOverride ||
          "linear-gradient(0deg, rgba(171,173,174,1) 0%, rgba(228,230,233,1) 16%, rgba(228,230,233,1) 85%, rgba(171,173,174,1) 100%)",
      }
    );

    return () => {
      setCurrentSlide(null);
      setFilteredSlidesConfig(null);
      setExitButtonVisible(null);
      setBackgroundOverride(null);
    };
  }, []);

  const updateIndex = (newIndex) => {
    if (newIndex > slideIndex) {
      setDirection("right");
      setAnimateOut(true);
      setAnimateIn(false);

      setTimeout(() => {
        setDirection("left");
        setAnimateIn(true);
        setAnimateOut(false);
        setSlideIndex(newIndex);
        setExitButtonVisible(newIndex === filteredSlidesConfig.slides.length - 1);
        if (!currentSlide.exitButtonText && newIndex >= filteredSlidesConfig.slides.length - 1) {
          setNextEnabled(true);
        } else {
          setNextEnabled(newIndex < filteredSlidesConfig.slides.length - 1);
        }
        setPreviousEnabled(newIndex > 0);
        setCurrentSlide(filteredSlidesConfig.slides[newIndex]);
        setBackgroundOverride(filteredSlidesConfig.slides[newIndex].backgroundOverride || filteredSlidesConfig.backgroundOverride);

        setPaperProps(
          filteredSlidesConfig.slides[newIndex].paper || {
            background:
              backgroundOverride ||
              "linear-gradient(0deg, rgba(171,173,174,1) 0%, rgba(228,230,233,1) 16%, rgba(228,230,233,1) 85%, rgba(171,173,174,1) 100%)",
          }
        );
      }, AnimationTime * 1000);
    } else {
      setDirection("left");
      setAnimateOut(true);
      setAnimateIn(false);

      setTimeout(() => {
        setDirection("right");
        setAnimateIn(true);
        setAnimateOut(false);
        setSlideIndex(newIndex);
        setExitButtonVisible(newIndex === filteredSlidesConfig.slides.length - 1);
        if (!currentSlide.exitButtonText && newIndex >= filteredSlidesConfig.slides.length - 1) {
          setNextEnabled(true);
        } else {
          setNextEnabled(newIndex < filteredSlidesConfig.slides.length - 1);
        }
        setPreviousEnabled(newIndex > 0);
        setCurrentSlide(filteredSlidesConfig.slides[newIndex]);
        setBackgroundOverride(filteredSlidesConfig.slides[newIndex].backgroundOverride || filteredSlidesConfig.backgroundOverride);

        setPaperProps(
          filteredSlidesConfig.slides[newIndex].paper || {
            background:
              backgroundOverride ||
              "linear-gradient(0deg, rgba(171,173,174,1) 0%, rgba(228,230,233,1) 16%, rgba(228,230,233,1) 85%, rgba(171,173,174,1) 100%)",
          }
        );
      }, AnimationTime * 1000);
    }

    // If there is not exist button text defined and we're at the end of the list enable to next button as an exit

    playSound();
  };

  const playSound = (sound = "button_press") => {
    Events.audio.playSound.emit({ src: sound, volume: 1 });
  };

  const onNextClick = () => {
    // Use exit vis state to trigger exit event on next button
    if (exitButtonVisible) {
      return exitEvent.emit();
    }
    if (!nextEnabled) return;
    updateIndex(slideIndex + 1);
  };

  const onPreviousClick = () => {
    if (!previousEnabled) return;
    updateIndex(slideIndex - 1);
  };

  const getContentComponent = ({ item, i, lastItem = {}, nextItem = {} }) => {
    switch (item.type) {
      case "bullet":
      case "text":
        let inlineStyle = {};
        if (!item.size) item.size = "normal";
        if (item.inline) inlineStyle = textStyles.inline;
        if (!item.color) item.color = "default";
        console.log(textStyles[item.size]);
        return (
          <Fragment>
            {item.type === "bullet" ? <Icon icon={faCircle} /> : null}
            <Typography
              key={i}
              sx={{
                ...textStyles.text,
                ...textStyles[item.size],
                ...textStyles[item.color],
                ...inlineStyle,
              }}
            >
              {i18n(item.value)}
            </Typography>
          </Fragment>
        );
      case "image":
        // Check if bottom/top margins are needed
        let marginTop = {};
        let marginBottom = filteredSlidesConfig.marginBottom;
        if (!item.fullPage && lastItem.type) marginTop = imageStyles.marginTop;
        if (!item.fullPage && nextItem.type && !marginBottom) marginBottom = imageStyles.marginBottom;

        return (
          <CardMedia
            key={i}
            image={item.value}
            style={{
              backgroundSize: "contain",
            }}
            sx={{
              ...(item.fullPage ? fullPageStyles.image : imageStyles.image),
              ...marginTop,
              ...marginBottom,
            }}
          ></CardMedia>
        );
      case "button":
        return (
          <Button
            variant={item.variant || "contained"}
            onClick={item.handler}
            sx={{
              position: "absolute",
              bottom: isMobile ? theme.spacing(16) : theme.spacing(11),
            }}
          >
            {item.text}
          </Button>
        );
      default:
        return;
    }
  };

  const updateAnimation = () => {
    let animationObject = {
      animationIterationCount: 1,
      animationFillMode: "forwards",
    };
    if (animateIn && direction === "right") animationObject.animation = `panInFromLeft ${AnimationTime}s`;
    if (animateIn && direction === "left") animationObject.animation = `panInFromRight ${AnimationTime}s`;
    if (animateOut && direction === "right") animationObject.animation = `panOutFromLeft ${AnimationTime}s`;
    if (animateOut && direction === "left") animationObject.animation = `panOutFromRight ${AnimationTime}s`;
    return animationObject;
  };

  return (
    <GameDialog
      open={open}
      buttonText={null}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      classes={{
        dialog: {
          zIndex: 1200,
          padding: isFullscreen && isMobile ? theme.spacing(0) : theme.spacing(2.5),
        },
        paper: paperProps,
      }}
      isFullscreen={isFullscreen || isMobile}
      isMobile={isMobile}
    >
      {currentSlide ? (
        <Box
          sx={{
            width: "75vw",
            height: "630px",
            textAlign: "center",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            overflow: "hidden",
            [theme.breakpoints.up("xl")]: {
              width: "45vw",
              maxWidth: "1000px",
            },
            ...(isFullscreen || isMobile
              ? {
                  width: "100%",
                  height: "100%",
                }
              : {
                  [theme.breakpoints.down("md")]: {
                    width: "100%",
                  },
                }),
          }}
        >
          {/* Next Button */}
          <Box
            onClick={onNextClick}
            sx={{
              position: "absolute",
              right: 0,
              zIndex: 10,
              width: isMobile ? theme.spacing(3) : theme.spacing(9),
              height: isMobile ? theme.spacing(5.5) : theme.spacing(13),
              color: theme.palette.dsPrimary.v300,
              display: isArrowsEnabled ? "flex" : "none",
              justifyContent: "flex-end",
              "&:hover": {
                opacity: 0.8,
                color: theme.palette.dsPrimary.v300,
              },
            }}
          >
            <Icon
              icon={faChevronRight}
              style={{
                height: "100%",
                fontSize: isMobile ? theme.spacing(10) : theme.spacing(15),
                cursor: nextEnabled ? "pointer" : "arrow",
                color: "inherit",
                opacity: 0.8,
                ...(!nextEnabled && {
                  opacity: 0.1,
                  color: theme.palette.dsSurface.v400,
                  "&:hover": null,
                }),
              }}
            />
          </Box>

          {/* Previous Button */}
          <Box
            onClick={onPreviousClick}
            sx={{
              position: "absolute",
              left: 0,
              zIndex: 10,
              width: isMobile ? theme.spacing(3) : theme.spacing(9),
              height: isMobile ? theme.spacing(5.5) : theme.spacing(13),
              color: theme.palette.dsPrimary.v300,
              display: isArrowsEnabled ? "flex" : "none",
              justifyContent: "start",
              "&:hover": {
                opacity: 0.8,
                color: theme.palette.dsPrimary.v300,
              },
            }}
          >
            <Icon
              icon={faChevronLeft}
              style={{
                height: "100%",
                fontSize: isMobile ? theme.spacing(10) : theme.spacing(15),
                cursor: previousEnabled ? "pointer" : "arrow",
                color: "inherit",
                opacity: 0.8,
                ...(!previousEnabled && {
                  opacity: 0.1,
                  color: theme.palette.dsSurface.v200,
                  "&:hover": null,
                }),
              }}
            />
          </Box>

          {/* Heading */}
          {currentSlide.heading ? (
            <Typography
              sx={{
                textTransform: "uppercase",
                width: "100%",
                fontSize: theme.spacing(4),
                textAlign: "center",
                color: theme.palette.dsSurface.v300,
                position: "absolute",
                top: theme.spacing(2),
                left: 0,
                fontFamily: "Moki-Base",
                ...(isMobile
                  ? {
                      "@media (orientation: portrait)": {
                        top: theme.spacing(2),
                      },
                      "@media (orientation: landscape)": {
                        top: theme.spacing(0),
                      },
                    }
                  : {
                      [theme.breakpoints.down("md")]: {
                        top: theme.spacing(2),
                      },
                    }),
              }}
            >
              {i18n(currentSlide.heading)}
            </Typography>
          ) : null}

          {/* Content Parsing */}
          <Box
            sx={{
              marginBottom: theme.spacing(5),
              width: "100%",
              height: !!slidesConfig.forceFullPageContent ? "100%" : "fit-content",
              paddingLeft: isMobile ? (isFullscreen ? theme.spacing(0) : theme.spacing(2)) : theme.spacing(5),
              paddingRight: isMobile ? (isFullscreen ? theme.spacing(0) : theme.spacing(2)) : theme.spacing(5),
              display: "flex",
              justifyContent: "center",
              animationIterationCount: 1,
              animationFillMode: "forwards",
              animationTimingFunction: "ease",
              boxSizing: "border-box",
              ...(isMobile && {
                "@media (orientation: portrait)": {
                  marginTop: theme.spacing(0),
                },
                "@media (orientation: landscape)": {
                  marginTop: theme.spacing(0),
                },
              }),
              ...(isScrollView && {
                overflowY: "scroll",
                "&::-webkit-scrollbar": {
                  width: "10px",
                  height: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#0089b5",
                  borderRadius: "0px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#39c2f0",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#ffffff",
                  borderRadius: "0px",
                  boxShadow: "inset -30px -30px 12px #c3d9eb",
                },
              }),
              ...updateAnimation(),
              "@keyframes panInFromLeft": {
                "0%": { transform: "translate(-100vw, 0)" },
                "100%": { transform: "translate(0vw, 0)" },
              },
              "@keyframes panInFromRight": {
                "0%": { transform: "translate(100vw, 0)" },
                "100%": { transform: "translate(0vw, 0)" },
              },
              "@keyframes panOutFromLeft": {
                "0%": { transform: "translate(0vw, 0)" },
                "100%": { transform: "translate(-100vw, 0)" },
              },
              "@keyframes panOutFromRight": {
                "0%": { transform: "translate(0vw, 0)" },
                "100%": { transform: "translate(100vw, 0)" },
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "inherit",
                position: "relative",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                margin: slidesConfig.marginOverride || "",
              }}
            >
              {currentSlide.content.map((item, i) => {
                return getContentComponent({
                  item,
                  i,
                  lastItem: currentSlide.content[i - 1] || {},
                  nextItem: currentSlide.content[i + 1] || {},
                });
              })}

              {/* Exit Button */}
              {currentSlide.exitButtonText ? (
                <Button
                  // className={exitButtonStyle.root}
                  variant="contained"
                  onClick={() => {
                    playSound();
                    exitEvent.emit();
                  }}
                  sx={{
                    display: exitButtonVisible ? "block" : "none",
                    width: theme.spacing(20),
                    position: "absolute",
                    left: isMobile ? "inherit" : "calc(50% - 80px)",
                    bottom: isMobile ? theme.spacing(12) : theme.spacing(-20),
                    height: theme.spacing(7),
                    fontSize: theme.spacing(3),
                  }}
                >
                  {currentSlide.exitButtonText}
                </Button>
              ) : null}
            </Box>
          </Box>
        </Box>
      ) : null}

      {/* Slide Counter */}
      {filteredSlidesConfig ? (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "calc(100% - 48px)", // subtract the padding from parent of parent
            paddingLeft: isMobile ? "10%" : "30%",
            paddingRight: isMobile ? "10%" : "30%",
            boxSizing: "border-box",
          }}
        >
          {filteredSlidesConfig.slides.length > 1 ? (
            <CounterBar
              size={20}
              styles={scoreBarOverrides}
              count={slideIndex + 1}
              total={filteredSlidesConfig.slides.length}
              spacing={16}
              disableTooltip={true}
            />
          ) : null}
        </Box>
      ) : null}
    </GameDialog>
  );
};
